import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Music, UtensilsCrossed, Star, Megaphone, Gamepad2, Disc3, Loader2, Lock, User, Phone, ArrowRight, CalendarDays } from 'lucide-react';
import GuestOtpModal from '../components/guest/GuestOtpModal';
import { guestGet, guestSet } from '../lib/guestStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Static motion props (module-scope so React doesn't allocate new objects every render) ---
const PULSE_ANIMATE = { scale: [1, 1.2, 1] };
const PULSE_TRANSITION = { duration: 8, repeat: Infinity };
const FORM_INITIAL = { opacity: 0, y: 12 };
const FORM_ANIMATE = { opacity: 1, y: 0 };
const FORM_TRANSITION = { duration: 0.4 };
const TILES_VARIANTS = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const TILE_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};
const TILE_TAP = { scale: 0.98 };

const VenueLandingPage = () => {
  const { venueCode } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guest identity — collected BEFORE showing tiles so we know who's interacting.
  // Persisted at TWO levels so downstream pages don't re-prompt:
  //   1. venue-level (`guest_venue_<venueCode>`) — covers permanent-QR re-scans
  //   2. session-level (`guest_session_<liveCode>`) — what GuestRequestPage already reads
  const VENUE_KEY = `guest_venue_${venueCode}`;
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [infoSubmitted, setInfoSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // OTP gate — when the venue requires SMS verification, we render the OTP
  // modal as the SOLE entry surface (no separate name/phone form first).
  // On verify success, the guest_token is mirrored into the same
  // session-level key GuestRequestPage reads, so the "Request a Song"
  // tile sails through without re-prompting.
  const [otpOpen, setOtpOpen] = useState(false);
  const [pendingGuest, setPendingGuest] = useState(null); // {name, phone} captured pre-OTP

  useEffect(() => {
    const stored = guestGet(VENUE_KEY);
    if (stored?.name && stored?.phone) {
      setGuestName(stored.name);
      setGuestPhone(stored.phone);
      setInfoSubmitted(true);
    }
  }, [VENUE_KEY]);

  // NOTE: When wa_otp_required is true we now render the OTP form INLINE on
  // the landing page (below the venue splash) so guests always see the
  // venue's logo + name while verifying. The overlay `otpOpen` modal is
  // only used for the "Change name & phone" re-verify path.

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/venue/${venueCode}/info`);
        setInfo(r.data);
      } catch (err) {
        console.warn('[Venue] info failed:', err);
        setError(err?.response?.status === 404 ? 'Venue not found' : 'Could not load venue');
      } finally {
        setLoading(false);
      }
    })();
  }, [venueCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }
  if (error || !info) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center">
        <Disc3 className="w-12 h-12 text-gray-700 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">{error || 'Venue not found'}</h1>
        <p className="text-gray-500 text-sm">Double-check the QR code or ask the venue staff.</p>
      </div>
    );
  }

  const { venue_name, venue_logo_url, host_features = {}, live_session, offers = [], wa_otp_required, client_type } = info;
  const liveCode = live_session?.code;
  const sessionFlags = live_session || {};
  const isAi = client_type === 'ai';
  // Brand the tiles with the venue's own profile photo when they have one;
  // fall back to the curated stock image per tile otherwise.
  const tileImg = (fallback) => venue_logo_url || fallback;

  // Persist the guest identity + (optionally) the OTP token into the same
  // session-level sessionStorage key that GuestRequestPage reads on `/request/:code`.
  // This is what carries the verified state across the tile-tap navigation.
  const persistGuestIdentity = ({ name, phone, token = '' }) => {
    const payload = { name, phone, at: new Date().toISOString() };
    guestSet(VENUE_KEY, payload);
    // Also stash plain name/phone so GuestGamesPage and other guest surfaces
    // hydrate without a fresh prompt.
    guestSet('guestName', name);
    guestSet('guestPhone', phone);
    if (liveCode) {
      guestSet(`guest_session_${liveCode}`, { ...payload, token });
    }
  };

  const handleOtpVerified = ({ guest_token, phone, name }) => {
    persistGuestIdentity({
      name: name || pendingGuest?.name || guestName,
      phone: phone || pendingGuest?.phone || guestPhone,
      token: guest_token,
    });
    if (phone) setGuestPhone(phone);
    if (name) setGuestName(name);
    setOtpOpen(false);
    setPendingGuest(null);
    setInfoSubmitted(true);
  };

  // Helper: a tile is HIDDEN when the host or DJ has explicitly turned the feature off.
  // It's only shown-as-disabled (locked) when the feature is enabled BUT the DJ isn't live yet.
  const requestsOn  = !!host_features.song_requests && sessionFlags.requests_enabled  !== false;
  const shoutoutsOn = !!host_features.shoutouts     && sessionFlags.shoutouts_enabled !== false;
  const menusOn     = !!host_features.menus;
  const gamesOn     = !!host_features.games         && sessionFlags.games_enabled     !== false;
  const reviewsOn   = host_features.reviews !== false && sessionFlags.reviews_enabled !== false;
  const eventsOn    = !!host_features.events;

  // Tile definitions. Each tile has `show` (render at all) and `enabled` (clickable / not locked).
  const tiles = [
    {
      key: 'request',
      show: requestsOn,
      enabled: !!liveCode,
      requiresLive: true,
      label: 'Request a Song',
      tag: 'Make the DJ play it',
      img: tileImg('https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&w=900'),
      accent: '#FF007F',
      Icon: Music,
      to: liveCode ? `/request/${liveCode}` : null,
      state: { startMode: 'request' },  // jump straight into song-search on land
    },
    {
      key: 'shoutout',
      show: shoutoutsOn,
      // AI venues have no live request session — shoutouts are host-scoped and
      // always available. Commercial venues keep the session-gated flow.
      enabled: isAi ? true : !!liveCode,
      requiresLive: !isAi,
      label: 'Send a Shoutout',
      tag: 'Surprise your crew',
      img: tileImg('https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&w=900'),
      accent: '#9D4CDD',
      Icon: Megaphone,
      to: isAi ? `/v/${venueCode}/shoutout` : (liveCode ? `/request/${liveCode}` : null),
      state: isAi ? undefined : { startMode: 'shoutout' },  // jump straight into the shoutout composer
    },
    {
      key: 'menu',
      show: menusOn,
      enabled: true,
      requiresLive: false,
      label: 'View the Menu',
      tag: 'Food · drinks · vibes',
      img: tileImg('https://images.pexels.com/photos/2664216/pexels-photo-2664216.jpeg?auto=compress&w=900'),
      accent: '#00FFFF',
      Icon: UtensilsCrossed,
      to: liveCode ? `/menu/${liveCode}` : `/menu-public/${venueCode}`,
    },
    {
      key: 'games',
      show: gamesOn,
      enabled: !!liveCode,
      requiresLive: true, // games still require a session code today
      label: 'Play & Win',
      tag: 'Win a coupon',
      img: tileImg('https://images.pexels.com/photos/278918/pexels-photo-278918.jpeg?auto=compress&w=900'),
      accent: '#39FF14',
      Icon: Gamepad2,
      to: liveCode ? `/play/${liveCode}` : null,
    },
    {
      key: 'review',
      show: reviewsOn,
      enabled: true,
      requiresLive: false,
      label: 'Leave a Review',
      tag: 'Tell us how we did',
      img: tileImg('https://images.pexels.com/photos/3201921/pexels-photo-3201921.jpeg?auto=compress&w=900'),
      accent: '#FFD700',
      Icon: Star,
      to: liveCode ? `/review/${liveCode}` : `/review-public/${venueCode}`,
    },
    {
      key: 'events',
      show: eventsOn,
      enabled: true,
      requiresLive: false,
      label: 'Upcoming Events',
      tag: 'RSVP & save your spot',
      img: tileImg('https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&w=900'),
      accent: '#FF007F',
      Icon: CalendarDays,
      to: `/v/${venueCode}/events`,
    },
  ].filter((t) => t.show);  // hide any feature the host/DJ explicitly turned OFF

  return (
    <div className="min-h-screen relative overflow-hidden bg-black" data-testid="venue-landing-page">
      {/* Backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${venue_logo_url || 'https://images.pexels.com/photos/3945667/pexels-photo-3945667.jpeg?auto=compress&w=1200'})` }}
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />
      <motion.div
        aria-hidden
        className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#FF007F]/22 blur-[140px]"
        animate={PULSE_ANIMATE}
        transition={PULSE_TRANSITION}
      />

      <div className="relative max-w-md mx-auto px-5 pt-8 pb-12">
        {/* Header */}
        <div className="text-center mb-7">
          {venue_logo_url ? (
            <img src={venue_logo_url} alt={venue_name} className="w-20 h-20 rounded-2xl object-cover mx-auto mb-3" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-white/5 mx-auto mb-3 flex items-center justify-center">
              <Disc3 className="w-10 h-10 text-[#FF007F]" />
            </div>
          )}
          <p className="text-[10px] uppercase tracking-[0.45em] text-white/50 font-['JetBrains_Mono'] mb-1">
            Welcome to
          </p>
          <h1 className="text-5xl sm:text-6xl font-black text-white font-['Outfit'] tracking-tight">
            {venue_name}
          </h1>
          {liveCode ? (
            <p className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#39FF14] font-['JetBrains_Mono'] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" /> DJ is live
            </p>
          ) : (
            <p className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-gray-500 font-['JetBrains_Mono']">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-600" /> Music is off — explore the menu &amp; games
            </p>
          )}
        </div>

        {/* Offers ticker */}
        {offers.length > 0 && (
          <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-2xl px-4 py-3 mb-6">
            <p className="text-[9px] uppercase tracking-[0.35em] text-[#FFD700] font-['JetBrains_Mono'] font-bold mb-1">
              Tonight&apos;s offers
            </p>
            <p className="text-white text-sm font-['Outfit']">{offers[0]}</p>
          </div>
        )}

        {/* === Entry gate ===
            Three states:
              1. infoSubmitted → tile grid
              2. SMS OTP required → no inline form (the OTP modal IS the entry)
              3. Otherwise → name/phone form, then tap CONTINUE to reach tiles */}
        {infoSubmitted ? null : (wa_otp_required && liveCode) ? (
          <GuestOtpModal
            inline
            sessionCode={liveCode}
            defaultName={pendingGuest?.name || guestName}
            defaultPhone={pendingGuest?.phone || guestPhone}
            onVerified={handleOtpVerified}
          />
        ) : (
          <motion.form
            onSubmit={(e) => {
              e.preventDefault();
              const name = guestName.trim();
              const phone = guestPhone.trim();
              if (!name || phone.length < 7) return;
              setSubmitting(true);
              // When the venue requires SMS OTP AND we have a live session
              // (OTP routes are session-scoped), gate the tiles behind the
              // OTP modal. Otherwise persist + reveal tiles immediately.
              if (wa_otp_required && liveCode) {
                setPendingGuest({ name, phone });
                setOtpOpen(true);
                setSubmitting(false);
                return;
              }
              persistGuestIdentity({ name, phone });
              setInfoSubmitted(true);
              setSubmitting(false);
            }}
            initial={FORM_INITIAL}
            animate={FORM_ANIMATE}
            transition={FORM_TRANSITION}
            className="bg-white/[0.04] border border-white/12 rounded-3xl p-5 backdrop-blur-xl"
            data-testid="guest-info-gate"
          >
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF007F] font-['JetBrains_Mono'] font-bold mb-1">
              Quick intro
            </p>
            <h2 className="text-xl font-bold text-white font-['Outfit'] mb-1">
              Before we get started…
            </h2>
            <p className="text-gray-400 text-[13px] leading-relaxed mb-5">
              Tell us your name &amp; mobile number so the DJ knows who&apos;s shouting requests, and we can ping you if you win a game.
            </p>

            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] font-bold mb-1.5">
              Name
            </label>
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-3 mb-4">
              <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Your first name"
                maxLength={40}
                className="bg-transparent text-white text-base py-3 flex-1 outline-none placeholder:text-gray-600"
                data-testid="guest-info-name"
                autoFocus
              />
            </div>

            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] font-bold mb-1.5">
              Mobile number
            </label>
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-3 mb-5">
              <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                type="tel"
                inputMode="tel"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value.replace(/[^\d+\s-]/g, ''))}
                placeholder="+91 98765 43210"
                maxLength={20}
                className="bg-transparent text-white text-base py-3 flex-1 outline-none placeholder:text-gray-600"
                data-testid="guest-info-phone"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !guestName.trim() || guestPhone.trim().length < 7}
              className="w-full bg-[#FF007F] hover:bg-[#CC0066] disabled:bg-white/10 disabled:text-gray-600 text-white font-bold tracking-wider rounded-full h-12 flex items-center justify-center gap-2 transition-all"
              data-testid="guest-info-submit"
            >
              CONTINUE <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-[10px] text-gray-600 text-center mt-4 leading-relaxed">
              We only share your details with this venue. Cleared the moment you close this tab.
            </p>
          </motion.form>
        )}

        {/* Tile grid — only rendered AFTER name + phone (and optional OTP) is captured */}
        {infoSubmitted && (
        <>
        <motion.div
          className="space-y-3"
          initial="hidden"
          animate="show"
          variants={TILES_VARIANTS}
        >
          {tiles.map((t) => {
            const Icon = t.Icon;
            const disabled = !t.enabled;
            return (
              <motion.button
                key={t.key}
                onClick={() => t.to && navigate(t.to, t.state ? { state: t.state } : undefined)}
                disabled={disabled}
                variants={TILE_VARIANTS}
                whileTap={!disabled ? TILE_TAP : undefined}
                className={`w-full relative overflow-hidden rounded-3xl border text-left group h-32 ${
                  disabled ? 'border-white/5 opacity-60 cursor-not-allowed' : 'border-white/10 hover:border-white/20'
                }`}
                data-testid={`venue-tile-${t.key}`}
              >
                <div
                  aria-hidden
                  className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-110 transition-transform duration-700"
                  style={{ backgroundImage: `url(${t.img})` }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(135deg, ${t.accent}55 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 100%)` }}
                />
                <div className="relative h-full p-4 flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-2xl backdrop-blur-md border border-white/20 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${t.accent}30` }}
                  >
                    <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.3em] font-['JetBrains_Mono'] font-bold mb-1" style={{ color: t.accent }}>
                      {t.tag}
                    </p>
                    <h3 className="text-white font-['Outfit'] font-black text-xl tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]">
                      {t.label}
                    </h3>
                    {disabled && t.requiresLive && (
                      <p className="text-[10px] text-white/60 mt-1 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Available when the DJ is live
                      </p>
                    )}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </motion.div>

        {/* Tiny "Hi, <name>" reassurance + ability to fix mistakes */}
        <div className="text-center mt-6">
          <p className="text-[11px] text-gray-500 font-['Outfit']">
            Hi <span className="text-white font-semibold">{guestName}</span> · ready when you are
          </p>
          <button
            onClick={() => {
              // When OTP is mandatory, "change name & phone" means re-verify
              // (the old name/phone form is hidden in OTP-required mode).
              setInfoSubmitted(false);
              if (wa_otp_required && liveCode) {
                setPendingGuest(null);
                setOtpOpen(true);
              }
            }}
            className="text-[10px] text-gray-600 underline-offset-4 hover:underline mt-1"
            data-testid="guest-info-edit"
          >
            Not you? Change name &amp; phone
          </button>
        </div>
        </>
        )}
      </div>

      {otpOpen && liveCode && (
        <GuestOtpModal
          sessionCode={liveCode}
          defaultName={pendingGuest?.name || guestName}
          defaultPhone={pendingGuest?.phone || guestPhone}
          onVerified={handleOtpVerified}
          onCancel={() => {
            // When OTP is mandatory there's no escape hatch — closing the
            // modal would strand the guest on an empty page. We simply
            // no-op the cancel so the modal stays put until they verify.
            if (wa_otp_required && liveCode) return;
            setOtpOpen(false);
            setPendingGuest(null);
          }}
        />
      )}
    </div>
  );
};

export default VenueLandingPage;
